import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Drawer,
  ListItemButton,
  NameEmailAvatar,
} from '../../../../../../../ui';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { CustomSelect } from '../../../../../../../ui/custom_react_select/custom_select';
import { useDebounce } from '../../../../../../../utils/hooks/useDebounce';
import { IInterviewResult } from '../../Results/Hooks';
import { StatusToggle } from '../Components';
import { useInviteCandidates } from './Hooks';

export interface Applicant {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  avatar?: string;
  applicantStatus?: string;
  interviewType?: 'Virtual' | 'In-Person';
}

interface IInviteCandidates {
  isOpen: boolean;
  onCloseDrawer: () => void;
  interviewRoundID: string;
  onInvitesSent: () => Promise<void>;
  isNotFirstRound?: boolean;
  results: IInterviewResult[];
}

const APPLICANT_STATUSES = [
  'Shortlisted',
  'New',
  'Outstanding',
  'Suitable',
  'Waitlisted',
  'Interview',
  'Success',
  'Offer-Extended',
  'Offer-Declined',
];
export const InviteCandidates: React.FC<IInviteCandidates> = ({
  isOpen,
  onCloseDrawer,
  interviewRoundID,
  onInvitesSent,
  isNotFirstRound,
  results,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  // For rounds after the first, candidates come from the previous round's
  // (already fully-loaded) results rather than a separate paginated API —
  // filter that fixed list client-side. Otherwise, `interviewApplicants`
  // is the general candidate pool, filtered/paginated server-side.
  const usingPreviousRoundResults = !!isNotFirstRound && results?.length > 0;

  const [candidateTypes, setCandidateTypes] = React.useState<
    Record<string, 'Virtual' | 'In-Person'>
  >({});

  const {
    interviewApplicants,
    selectedCandidates,
    handleSelect,
    handleRemove,
    handleSelectAll,
    handleRemoveAll,
    sendAllInvites,
    newSelections,
    setSearchObject,
    pageNumber,
    setPageNumber,
    totalPages,
    totalRecords,
  } = useInviteCandidates({ interviewRoundID, isOpen, candidateTypes });

  const [localName, setLocalName] = React.useState('');
  const [localEmail, setLocalEmail] = React.useState('');
  const [applicantStatus, setApplicantStatus] = React.useState<string[]>(
    usingPreviousRoundResults ? [] : ['Shortlisted'],
  );

  const debouncedName = useDebounce(localName, 500);
  const debouncedEmail = useDebounce(localEmail, 500);

  // Push the (debounced) search criteria to the hook so it's sent to the
  // server — the API paginates the full candidate pool, so filtering must
  // happen server-side rather than only on the single page already fetched.
  React.useEffect(() => {
    setSearchObject({
      name: debouncedName,
      email: debouncedEmail,
      interviewStatus: applicantStatus,
    });
  }, [debouncedName, debouncedEmail, applicantStatus, setSearchObject]);

  const filters = {
    name: localName,
    email: localEmail,
    applicantStatus,
  };

  const applicantStatusOptions = APPLICANT_STATUSES.map((status) => ({
    label: status,
    value: status,
  }));

  const handleStatusChange = (selected: any) => {
    setApplicantStatus(selected ? selected.map((item: any) => item.value) : []);
  };

  const handleSend = async () => {
    try {
      await sendAllInvites();
      onInvitesSent();
    } catch (e) {
      console.error(e);
    }
  };

  const handleTypeChange = (
    candidateId: string,
    value: 'Virtual' | 'In-Person',
  ) => {
    setCandidateTypes((prev) => ({ ...prev, [candidateId]: value }));
  };

  const getTypeForCandidate = (id: string): string => {
    return candidateTypes[id] || 'Virtual';
  };

  React.useEffect(() => {
    // Default to "Shortlisted" whenever pulling from the general candidate
    // pool; previous-round completed results use interview statuses instead.
    setApplicantStatus(usingPreviousRoundResults ? [] : ['Shortlisted']);
  }, [usingPreviousRoundResults]);

  const filteredApplicants = React.useMemo(() => {
    if (!usingPreviousRoundResults) return interviewApplicants;

    const resultsAsCandidates = results
      .filter((result) => (
        result.user && result.user.email && result.status === 'Completed'
      ))
      .map((result) => ({
        id: result.jobApplicantId || result.user?.id || '',
        email: result.user?.email || '',
        firstName: result.user?.firstName || '',
        lastName: result.user?.lastName || '',
        applicantStatus:
          result.interviewApplicantStatus || result.status || 'Completed',
        interviewType: (result.interviewType === 'In-Person'
          ? 'In-Person'
          : 'Virtual') as 'Virtual' | 'In-Person',
        avatar: undefined,
      })) as Applicant[];

    const selectedIds = new Set(
      selectedCandidates.map((candidate) => candidate.id),
    );
    const selectedEmails = new Set(
      selectedCandidates.map((candidate) => candidate.email.toLowerCase()),
    );

    return resultsAsCandidates.filter((a) => {
      if (selectedIds.has(a.id) || selectedEmails.has(a.email.toLowerCase())) {
        return false;
      }

      const fullName = (a.firstName + ' ' + a.lastName).toLowerCase();
      const emailMatch = filters.email
        ? a.email.toLowerCase().includes(filters.email.toLowerCase())
        : true;
      const nameMatch = filters.name
        ? fullName.includes(filters.name.toLowerCase())
        : true;
      const statusMatch =
        filters.applicantStatus.length === 0 ||
        (a.applicantStatus &&
          filters.applicantStatus.some(
            (s) => s.toLowerCase() === a.applicantStatus!.toLowerCase(),
          ));

      return emailMatch && nameMatch && statusMatch;
    });
  }, [
    usingPreviousRoundResults,
    interviewApplicants,
    results,
    selectedCandidates,
    filters.email,
    filters.name,
    filters.applicantStatus,
  ]);

  return (
    <Drawer
        isOpen={isOpen}
        onClose={onCloseDrawer}
        title={t(SI.candidates.invite.drawerTitle)}
        width='w-full md:w-[600px]'
        footerContent={
          <div className='flex justify-end gap-5 w-full'>
            <Button label={t(SI.candidates.invite.cancel)} onClick={onCloseDrawer} variant='secondary' />
            <Button
                label={t(SI.candidates.invite.sendInvitations)}
                onClick={handleSend}
                variant='primary'
                disabled={newSelections.length === 0}
            />
          </div>
        }
    >
      <div className='space-y-4'>
        <div className='flex flex-col justify-between items-start w-full'>
          <div className='flex w-full items-center mb-3'>
            <div className='flex-1'>
              <h3 className='font-semibold text-base tracking-tight text-gray-900'>
                { t(SI.candidates.invite.availableCandidates) }
              </h3>
              <p className='text-sm text-gray-500'>
                { t(SI.candidates.invite.subtitle) }
              </p>
            </div>
            { interviewApplicants.length > 0 && (
              <Button
                  variant='link'
                  label={t(SI.candidates.invite.selectAll)}
                  onClick={handleSelectAll}
              />
            ) }
          </div>
          { /* Filter Controls */ }
          { (!isNotFirstRound || results?.length > 0) && (
            <div className='w-full rounded-sm border border-gray-200 bg-gradient-to-tr from-gray-50 to-white shadow-md px-6 py-5 flex flex-wrap items-end gap-6 transition duration-150'>
                <div className='flex flex-col gap-1 min-w-[180px] flex-1'>
                <label className='text-xs font-semibold text-gray-500'>
                    { t(SI.candidates.invite.filterName) }
                </label>
                <input
                      type='text'
                      placeholder={t(SI.candidates.invite.searchByName)}
                      value={localName}
                      onChange={(e) => setLocalName(e.target.value)}
                      className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition duration-100 placeholder-gray-400 shadow-sm hover:border-blue-400'
                />
              </div>
                <div className='flex flex-col gap-1 min-w-[180px] flex-1'>
                <label className='text-xs font-semibold text-gray-500'>
                    { t(SI.candidates.invite.filterEmail) }
                </label>
                <input
                      type='text'
                      placeholder={t(SI.candidates.invite.searchByEmail)}
                      value={localEmail}
                      onChange={(e) => setLocalEmail(e.target.value)}
                      className='border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white transition duration-100 placeholder-gray-400 shadow-sm hover:border-blue-400'
                />
              </div>
                <div className='flex flex-col gap-1 min-w-[180px] flex-1'>
                <label className='text-xs font-semibold text-gray-500 mb-1'>
                    { t(SI.candidates.invite.filterStatus) }
                </label>
                <CustomSelect
                      name='applicantStatus'
                      value={applicantStatus.map((item) => ({
                      label: item,
                      value: item,
                    }))}
                      options={applicantStatusOptions}
                      onChange={handleStatusChange}
                      isMulti
                      clearable
                      placeholder={t(SI.candidates.invite.filterByStatus)}
                />
              </div>
                <div className='flex flex-col gap-1'>
                <span className='invisible text-xs select-none'>Clear</span>
                <Button
                      type='button'
                      onClick={() => {
                        setLocalName('');
                        setLocalEmail('');
                        setApplicantStatus([]);
                      }}
                      label={t(SI.candidates.invite.clearFilters)}
                      variant='secondary'
                />
              </div>
              </div>
          ) }
        </div>
        { filteredApplicants.length === 0 ? (
          <p className='text-sm text-gray-500'>{ t(SI.candidates.invite.noCandidates) }</p>
        ) : (
          <ul className='space-y-2'>
            { filteredApplicants.map((app) => (
              <ListItemButton key={app.id} onClick={() => handleSelect(app)}>
                <NameEmailAvatar
                    email={app.email}
                    name={`${app.firstName} ${app.lastName}`}
                    avatar={app.avatar || ''}
                />
                <div className='flex-1 min-w-0 justify-end flex items-center gap-4'>
                  <div className='score text-sm text-gray-600 whitespace-nowrap'>
                    { results.find((r) => r.jobApplicantId === app.id)
                      ?.aiOverallScore?.score
                      ? `${
                        results.find((r) => r.jobApplicantId === app.id)
                          ?.aiOverallScore?.score
                      }%`
                      : '' }
                  </div>
                  <div className='text-sm text-gray-600 whitespace-nowrap'>
                    { app.applicantStatus }
                  </div>
                </div>
              </ListItemButton>
            )) }
          </ul>
        ) }
        { !usingPreviousRoundResults && totalRecords > 0 && (
          <div className='flex items-center justify-between w-full pt-1'>
            <span className='text-xs text-gray-500'>
              { totalRecords } candidate{ totalRecords !== 1 ? 's' : '' } found
            </span>
            <div className='flex items-center gap-3'>
              <Button
                  type='button'
                  variant='secondary'
                  label='Prev'
                  disabled={pageNumber <= 1}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
              />
              <span className='text-xs text-gray-500'>
                Page { pageNumber } of { totalPages }
              </span>
              <Button
                  type='button'
                  variant='secondary'
                  label='Next'
                  disabled={pageNumber >= totalPages}
                  onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
              />
            </div>
          </div>
        ) }
        <hr />
        <div className='flex justify-between items-center mt-4'>
          <h3 className='font-semibold text-base'>{ t(SI.candidates.invite.selectedCandidates) }</h3>
          { selectedCandidates.length > 0 && (
            <Button
                variant='link'
                label={t(SI.candidates.invite.removeAll)}
                onClick={handleRemoveAll}
            />
          ) }
        </div>
        { selectedCandidates.length === 0 ? (
          <p className='text-sm text-gray-500'>{ t(SI.candidates.invite.noSelected) }</p>
        ) : (
          <ul className='space-y-2'>
            { /* Show all selected candidates */ }
            { selectedCandidates.map((cand) => (
              <ListItemButton
                  key={cand.id}
                  isSelected
                  onClick={() => {}}
                  onRemove={() => handleRemove(cand)}
              >
                <div className='flex flex-col gap-2 border-b w-full pb-1'>
                  <div className='flex items-center justify-between w-full'>
                    <NameEmailAvatar
                        email={cand.email}
                        name={`${cand.firstName} ${cand.lastName}`}
                        avatar={cand.avatar || ''}
                    />
                    <div className='score text-sm text-gray-600'>
                      { results.find((r) => r.jobApplicantId === cand.id)
                        ?.aiOverallScore?.score
                        ? `${
                          results.find((r) => r.jobApplicantId === cand.id)
                            ?.aiOverallScore?.score
                        }%`
                        : '' }
                    </div>
                  </div>
                  <StatusToggle
                      options={['Virtual', 'In-Person']}
                      value={getTypeForCandidate(cand.id)}
                      onChange={(val) =>
                        handleTypeChange(cand.id, val as 'Virtual' | 'In-Person')
                      }
                  />
                </div>
              </ListItemButton>
            )) }
          </ul>
        ) }
      </div>
    </Drawer>
  );
};